import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { useIdleTimer } from '../hooks/useIdleTimer';
import toast from 'react-hot-toast';
import {
  LayoutDashboard,
  Box,
  Key,
  ArrowRightLeft,
  BookOpen,
  LogOut,
  Menu,
  X,
  User,
  Settings,
  Files,
  Users
} from 'lucide-react';

import { PERMISSIONS, hasPermission } from '../utils/permissions';

const Layout = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const userPermissions = user?.permissions || [];

  // Setup Idle Timer (10 minutes idle, 30 seconds warning)
  useIdleTimer({
    onIdle: () => {
      logout();
      navigate('/login');
      toast.error('Session expired due to inactivity', { id: 'idle-logout' });
    },
    onWarn: () => {
      toast('Your session will expire in 30 seconds due to inactivity. Move your mouse to stay logged in.', {
        icon: '⏳',
        duration: 30000,
        id: 'idle-warning',
        style: {
          borderRadius: '12px',
          background: '#0F172A',
          color: '#fff',
        },
      });
    },
    idleTime: process.env.REACT_APP_IDLE_TIME,
    warningTime: 30 * 1000,
  });

  const menuItems = [
    { path: '/dashboard', name: 'Dashboard', icon: LayoutDashboard },
    {
      path: '/api-credentials',
      name: 'API Credentials',
      icon: Key,
      permission: [PERMISSIONS.VIEW_API_KEYS, PERMISSIONS.MANAGE_API_KEYS]
    },
    {
      path: '/products',
      name: 'Products',
      icon: Box,
      permission: PERMISSIONS.MANAGE_SUBSCRIPTIONS
    },
    {
      path: '/transactions',
      name: 'Transactions',
      icon: ArrowRightLeft,
      permission: PERMISSIONS.VIEW_TRANSACTIONS
    },
    {
      path: '/bulk-transfer',
      name: 'Bulk Transfer',
      icon: Files,
      permission: PERMISSIONS.VIEW_TRANSACTIONS
    },
    {
      path: '/team',
      name: 'Team Management',
      icon: Users,
      permission: PERMISSIONS.MANAGE_TEAM
    },
    {
      path: '/settings',
      name: 'Settings',
      icon: Settings,
      permission: [PERMISSIONS.VIEW_SETTINGS, PERMISSIONS.MANAGE_SETTINGS, PERMISSIONS.MANAGE_WEBHOOKS]
    },
    { path: '/profile', name: 'Profile', icon: User },
    { path: '/documentation', name: 'Documentation', icon: BookOpen },
  ].filter(item => !item.permission || hasPermission(userPermissions, item.permission, user?.role));

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <div className="flex h-screen bg-background-light overflow-hidden font-sans text-slate-800">


      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm lg:hidden"
          onClick={closeMobileMenu}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-72 bg-white/80 backdrop-blur-xl border-r border-slate-200 
        transform transition-transform duration-300 ease-in-out lg:translate-x-0 flex flex-col
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex items-center justify-between h-20 px-6 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <img src="/icon.png" alt="Waya" className="w-8 h-8 object-contain" />
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">Waya B2B</h1>
          </div>
          <button onClick={closeMobileMenu} className="lg:hidden text-slate-500 hover:text-slate-700">
            <X size={24} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-6 px-4 space-y-1">
          {menuItems.map((item) => (
            <NavLink
              key={item.name}
              to={item.path}
              onClick={closeMobileMenu}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${isActive
                  ? 'bg-primary/10 text-primary shadow-sm'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon size={20} className={isActive ? "text-primary" : "text-slate-400"} />
                  {item.name}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-100">
          <button
            onClick={logout}
            className="flex items-center justify-center w-full gap-2 px-4 py-3 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors duration-200"
          >
            <LogOut size={18} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="h-20 bg-white/60 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 lg:px-8 z-10 hidden sm:flex">
          <div className="flex items-center gap-4">
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <Menu size={24} />
            </button>
            <h2 className="text-xl font-semibold text-slate-800">Dashboard Portal</h2>
          </div>

          <Link to="/profile" className="flex items-center gap-4 hover:bg-slate-50 p-1 rounded-lg transition-colors group">
            <div className="hidden md:flex flex-col items-end">
              <span className="text-sm font-medium text-slate-900 group-hover:text-primary transition-colors">{user?.firstName} {user?.lastName}</span>
              <span className="text-xs text-slate-500">{user?.role}</span>
            </div>
            <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 group-hover:border-primary/50 group-hover:text-primary transition-all">
              <User size={20} />
            </div>
          </Link>
        </header>

        {/* Mobile Header (visible only on small screens) */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:hidden z-10">
          <div className="flex items-center gap-2">
            <button
              onClick={toggleMobileMenu}
              className="p-2 -ml-2 text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              <Menu size={24} />
            </button>
            <img src="/icon.png" alt="Waya" className="w-6 h-6 object-contain" />
          </div>
          <Link to="/profile" className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 border border-slate-200">
            <User size={16} />
          </Link>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto p-4 sm:p-6 lg:p-8 bg-slate-50/50 relative">
          <div className="max-w-7xl mx-auto animate-fade-in relative z-10">
            <Outlet />
          </div>
          {/* Decorative background blur */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -z-10 translate-x-1/2 -translate-y-1/3 pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl -z-10 -translate-x-1/2 translate-y-1/3 pointer-events-none"></div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
